import React, { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  Plus, 
  Edit, 
  Copy, 
  Trash2, 
  Move, 
  ChevronDown, 
  ChevronUp, 
  Eye, 
  EyeOff, 
  Layout, 
  FileText, 
  Settings,
  Info,
  AlertCircle
} from 'lucide-react';
import ComponentLibrary, { ComponentType, PageComponent } from './ComponentLibrary';
import ComponentSettings from './ComponentSettings';
import ComponentPreview from './ComponentPreview';
import { savePageContent, Page } from '../../lib/pageService';

interface PageEditorProps {
  page: Page | null;
  onClose: () => void;
  onSave: () => void;
}

const PageEditor: React.FC<PageEditorProps> = ({ page, onClose, onSave }) => {
  const [pageData, setPageData] = useState<Partial<Page>>({
    title: '',
    slug: '',
    metaTitle: '',
    metaDescription: '',
    isPublished: false,
    isFeatured: false,
    template: 'default',
    content: '{}',
  });
  
  const [components, setComponents] = useState<PageComponent[]>([]);
  const [activeTab, setActiveTab] = useState<'content' | 'settings'>('content');
  const [showComponentLibrary, setShowComponentLibrary] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Load page data when page prop changes
  useEffect(() => {
    if (page) {
      console.log('Loading existing page data:', page);
      setPageData({
        id: page.id,
        title: page.title,
        slug: page.slug,
        metaTitle: page.metaTitle,
        metaDescription: page.metaDescription,
        isPublished: page.isPublished,
        isFeatured: page.isFeatured,
        template: page.template,
        content: page.content,
        author: page.author
      });
      
      // Parse components from content
      try {
        let pageContent;
        if (typeof page.content === 'string') {
          pageContent = JSON.parse(page.content);
        } else {
          pageContent = page.content;
        }
        
        if (pageContent.components && Array.isArray(pageContent.components)) {
          setComponents(pageContent.components);
        } else {
          // Initialize with empty components array if not found
          setComponents([]);
        }
      } catch (error) {
        console.error('Error parsing page content:', error);
        setComponents([]);
      }
    } else {
      // Reset form for new page
      console.log('Creating new page');
      setPageData({
        title: '',
        slug: '',
        metaTitle: '',
        metaDescription: '',
        isPublished: false,
        isFeatured: false,
        template: 'default',
        content: '{}',
        author: 'Admin'
      });
      setComponents([]);
    }
  }, [page]);
  
  // Generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };
  
  // Add a new component
  const handleAddComponent = (componentType: ComponentType) => {
    const newComponent: PageComponent = {
      id: `component-${Date.now()}`,
      type: componentType,
      title: getComponentTitle(componentType),
      order: components.length + 1,
      settings: getDefaultSettings(componentType),
      content: getDefaultContent(componentType)
    };
    
    setComponents([...components, newComponent]);
    setShowComponentLibrary(false);
    setSelectedComponent(newComponent.id);
  };
  
  // Get default title for a component type
  const getComponentTitle = (type: ComponentType): string => {
    switch (type) {
      case 'header': return 'Header';
      case 'footer': return 'Footer';
      case 'faq': return 'Sık Sorulan Sorular';
      case 'newsletter': return 'Bülten Kaydı';
      case 'bankComparison': return 'Banka Karşılaştırma';
      case 'bankList': return 'Banka Listesi';
      case 'loanCalculator': return 'Kredi Hesaplama';
      case 'costTable': return 'Maliyet Tablosu';
      case 'interestRates': return 'Faiz Oranları';
      case 'textBlock': return 'Metin Bloğu';
      case 'imageBlock': return 'Görsel Bloğu';
      case 'callToAction': return 'Aksiyon Çağrısı';
      default: return 'Yeni Bileşen';
    }
  };
  
  // Get default settings for a component type
  const getDefaultSettings = (type: ComponentType): any => {
    switch (type) {
      case 'header':
        return {
          logoText: 'KREDİPARK',
          backgroundColor: '#F7BA45',
          textColor: '#1e293b',
          isSticky: true,
          showLoginButton: true
        };
      case 'footer':
        return {
          logoText: 'KREDİPARK',
          slogan: 'Türkiye\'nin en kapsamlı kredi ve banka karşılaştırma platformu',
          backgroundColor: '#ffffff',
          textColor: '#1e293b'
        };
      case 'faq':
        return {
          title: 'Sık Sorulan Sorular',
          subtitle: 'Merak ettiğiniz tüm sorular ve cevapları',
          maxItems: 10,
          showCategories: true
        };
      case 'newsletter':
        return {
          title: 'Fırsatlardan Haberdar Olun',
          subtitle: 'En güncel banka kampanyaları ve fırsatlardan haberdar olun',
          buttonText: 'Abone Ol',
          showPhone: true,
          showEmail: true
        };
      case 'bankComparison':
        return {
          title: 'Banka Karşılaştırma',
          maxItems: 4,
          showFeatures: true
        };
      case 'bankList':
        return {
          title: 'Bankalar',
          subtitle: 'Çalıştığımız bankalar',
          autoScroll: true
        };
      case 'loanCalculator':
        return {
          title: 'Kredi Hesaplama',
          loanType: 'ihtiyac',
          showAmountSlider: true,
          showTermSlider: true
        };
      case 'textBlock':
        return {
          title: 'Metin Bloğu',
          showTitle: true,
          textAlign: 'left'
        };
      case 'imageBlock':
        return {
          title: 'Görsel Bloğu',
          showCaption: true,
          imageAlign: 'center'
        };
      case 'callToAction':
        return {
          title: 'Aksiyon Çağrısı',
          buttonText: 'Hemen Başvur',
          buttonColor: '#F7BA45',
          textColor: '#1e293b'
        };
      default:
        return {};
    }
  };
  
  // Get default content for a component type
  const getDefaultContent = (type: ComponentType): any => {
    switch (type) {
      case 'header':
        return {
          menuItems: [
            {
              label: 'Ana Sayfa',
              url: '/',
              isDropdown: false
            },
            {
              label: 'Krediler',
              url: '#',
              isDropdown: true,
              children: [
                {
                  label: 'İhtiyaç Kredisi',
                  url: '/ihtiyac-kredisi'
                },
                {
                  label: 'Konut Kredisi',
                  url: '/konut-kredisi'
                }
              ]
            },
            {
              label: 'Kredi Kartları',
              url: '/kredi-kartlari',
              isDropdown: false
            }
          ]
        };
      case 'footer':
        return {
          columns: [
            {
              title: 'Ürünler',
              links: [
                { label: 'İhtiyaç Kredisi', url: '/ihtiyac-kredisi' },
                { label: 'Kredi Kartları', url: '/kredi-kartlari' },
                { label: 'Mevduat', url: '/mevduat' }
              ]
            },
            {
              title: 'Kurumsal',
              links: [
                { label: 'Hakkımızda', url: '/hakkimizda' },
                { label: 'Gizlilik Politikası', url: '/gizlilik-politikasi' },
                { label: 'KVKK', url: '/kvkk-aydinlatma-metni' }
              ]
            }
          ],
          address: 'Kuştepe Mah. Mecidiyeköy Yolu Cad. Trump Tower No:12 İç Kapı No: 221 Şişli İstanbul',
          phone: '0(212) 909 1133',
          email: 'iletisim@kredipark.com',
          website: 'https://www.kredipark.com',
          social: {
            facebook: 'https://www.facebook.com/kredipark',
            instagram: 'https://www.instagram.com/kredipark',
            twitter: 'https://twitter.com/kredipark',
            linkedin: 'https://www.linkedin.com/company/kredipark'
          },
          copyrightText: `© ${new Date().getFullYear()} KrediPark. Tüm hakları saklıdır.`,
          companyInfo: 'Technoworks Bilgi Teknolojileri A.Ş.',
          mersisNo: '833118794400001'
        };
      case 'faq':
        return {
          faqs: [
            {
              question: 'Örnek soru?',
              answer: 'Örnek cevap.',
              category: 'Genel'
            }
          ]
        };
      case 'textBlock':
        return {
          text: '<p>Buraya metin ekleyin...</p>'
        };
      case 'imageBlock':
        return {
          imageUrl: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&q=80&w=1200&h=600',
          caption: 'Görsel açıklaması'
        };
      case 'callToAction':
        return {
          heading: 'Aksiyon Çağrısı Başlığı',
          description: 'Kısa açıklama metni',
          buttonUrl: '#'
        };
      default:
        return {};
    }
  };
  
  // Update component settings
  const handleUpdateComponent = (componentId: string, updates: Partial<PageComponent>) => {
    setComponents(prevComponents => 
      prevComponents.map(component => 
        component.id === componentId 
          ? { ...component, ...updates } 
          : component
      )
    );
  };
  
  // Delete a component
  const handleDeleteComponent = (componentId: string) => {
    if (confirm('Bu bileşeni silmek istediğinizden emin misiniz?')) {
      setComponents(prevComponents => 
        prevComponents.filter(component => component.id !== componentId)
      );
      if (selectedComponent === componentId) {
        setSelectedComponent(null);
      }
    }
  };
  
  // Duplicate a component
  const handleDuplicateComponent = (componentId: string) => {
    const componentToDuplicate = components.find(c => c.id === componentId);
    if (componentToDuplicate) {
      const newComponent = {
        ...componentToDuplicate,
        id: `component-${Date.now()}`,
        title: `${componentToDuplicate.title} (Kopya)`,
        order: components.length + 1
      };
      setComponents([...components, newComponent]);
    }
  };
  
  // Move component up in order
  const handleMoveUp = (componentId: string) => {
    const index = components.findIndex(c => c.id === componentId);
    if (index > 0) {
      const newComponents = [...components];
      [newComponents[index - 1], newComponents[index]] = [newComponents[index], newComponents[index - 1]];
      // Update order property
      newComponents.forEach((component, i) => {
        component.order = i + 1;
      });
      setComponents(newComponents);
    }
  };
  
  // Move component down in order
  const handleMoveDown = (componentId: string) => {
    const index = components.findIndex(c => c.id === componentId);
    if (index < components.length - 1) {
      const newComponents = [...components];
      [newComponents[index], newComponents[index + 1]] = [newComponents[index + 1], newComponents[index]];
      // Update order property
      newComponents.forEach((component, i) => {
        component.order = i + 1;
      });
      setComponents(newComponents);
    }
  };
  
  // Save the page
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      // Validate required fields
      if (!pageData.title) {
        setError('Sayfa başlığı gereklidir.');
        setIsSaving(false);
        return;
      }
      
      if (!pageData.slug) {
        setError('Sayfa URL slug gereklidir.');
        setIsSaving(false);
        return;
      }
      
      console.log('Preparing to save page with title:', pageData.title);
      
      // Parse existing content if it's a string
      let existingContent = {};
      try {
        if (typeof pageData.content === 'string') {
          existingContent = JSON.parse(pageData.content);
        } else if (pageData.content) {
          existingContent = pageData.content;
        }
      } catch (err) {
        console.error('Error parsing existing content:', err);
        existingContent = {};
      }
      
      // Prepare content with components
      const contentObj = {
        ...existingContent,
        components
      };
      
      // Convert to string for saving
      const contentString = JSON.stringify(contentObj, null, 2);
      
      console.log('Content prepared for saving:', contentString.substring(0, 100) + '...');
      
      // Save page
      const savedPage = await savePageContent({
        ...pageData,
        content: contentString
      });
      
      if (savedPage) {
        console.log('Page saved successfully:', savedPage);
        setSaveSuccess(true);
        setTimeout(() => {
          setSaveSuccess(false);
          onSave();
        }, 1500);
      } else {
        console.error('Failed to save page, no response from savePageContent');
        setError('Sayfa kaydedilirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Error saving page:', err);
      setError('Sayfa kaydedilirken bir hata oluştu: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-900/50 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose}></div>
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-800">
              {page ? 'Sayfa Düzenle' : 'Yeni Sayfa Oluştur'}: {pageData.title}
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="p-2 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-slate-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('content')}
                className={`px-4 py-3 font-medium transition-colors relative ${
                  activeTab === 'content' 
                    ? 'text-[#F7BA45]' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                İçerik
                {activeTab === 'content' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F7BA45]" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-4 py-3 font-medium transition-colors relative ${
                  activeTab === 'settings' 
                    ? 'text-[#F7BA45]' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Sayfa Ayarları
                {activeTab === 'settings' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#F7BA45]" />
                )}
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {activeTab === 'content' ? (
              <>
                {/* Components Panel */}
                <div className="w-full flex flex-col overflow-hidden">
                  <div className="p-4 flex-1 overflow-y-auto">
                    {components.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center p-8">
                        <Layout size={48} className="text-slate-300 mb-4" />
                        <h3 className="text-xl font-medium text-slate-700 mb-2">Henüz Bileşen Yok</h3>
                        <p className="text-slate-500 mb-6 max-w-md">
                          Bu sayfaya bileşen eklemek için aşağıdaki butona tıklayın ve bileşen kütüphanesinden bir bileşen seçin.
                        </p>
                        <button
                          onClick={() => setShowComponentLibrary(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                        >
                          <Plus size={20} />
                          <span>Bileşen Ekle</span>
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {components.map((component) => (
                          <div 
                            key={component.id}
                            className={`border rounded-xl transition-all ${
                              selectedComponent === component.id 
                                ? 'border-[#F7BA45] shadow-md' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="p-3 bg-slate-50 rounded-t-xl border-b border-slate-200 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Move size={16} className="text-slate-400 cursor-move" />
                                <span className="font-medium text-slate-700">{component.title}</span>
                                <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                                  {component.type}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleMoveUp(component.id)}
                                  disabled={components.indexOf(component) === 0}
                                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <ChevronUp size={16} />
                                </button>
                                <button
                                  onClick={() => handleMoveDown(component.id)}
                                  disabled={components.indexOf(component) === components.length - 1}
                                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <ChevronDown size={16} />
                                </button>
                                <button
                                  onClick={() => setSelectedComponent(selectedComponent === component.id ? null : component.id)}
                                  className="p-1 text-slate-400 hover:text-slate-700"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDuplicateComponent(component.id)}
                                  className="p-1 text-slate-400 hover:text-slate-700"
                                >
                                  <Copy size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteComponent(component.id)}
                                  className="p-1 text-slate-400 hover:text-red-600"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                            
                            {/* Component Preview */}
                            <div className="p-4">
                              <ComponentPreview component={component} />
                            </div>
                            
                            {/* Component Settings (if selected) */}
                            {selectedComponent === component.id && (
                              <div className="p-4 border-t border-slate-200 bg-slate-50">
                                <ComponentSettings 
                                  component={component}
                                  onChange={(updates) => handleUpdateComponent(component.id, updates)}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {/* Add Component Button */}
                        <div className="flex justify-center py-4">
                          <button
                            onClick={() => setShowComponentLibrary(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                          >
                            <Plus size={20} />
                            <span>Bileşen Ekle</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Component Library (if shown) */}
                  {showComponentLibrary && (
                    <div className="border-t border-slate-200 p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-slate-800">Bileşen Ekle</h3>
                        <button
                          onClick={() => setShowComponentLibrary(false)}
                          className="p-1 text-slate-400 hover:text-slate-700"
                        >
                          <X size={20} />
                        </button>
                      </div>
                      <ComponentLibrary onAddComponent={handleAddComponent} />
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Page Settings Tab
              <div className="w-full p-4 overflow-y-auto">
                <div className="max-w-3xl mx-auto space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">
                        Sayfa Başlığı
                      </label>
                      <input
                        type="text"
                        value={pageData.title || ''}
                        onChange={(e) => {
                          const title = e.target.value;
                          setPageData(prev => ({ 
                            ...prev, 
                            title,
                            slug: !page ? generateSlug(title) : prev.slug,
                            metaTitle: !page || !prev.metaTitle ? `${title} - KrediPark` : prev.metaTitle
                          }));
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">
                        URL Slug
                      </label>
                      <input
                        type="text"
                        value={pageData.slug || ''}
                        onChange={(e) => setPageData(prev => ({ ...prev, slug: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Meta Başlık
                    </label>
                    <input
                      type="text"
                      value={pageData.metaTitle || ''}
                      onChange={(e) => setPageData(prev => ({ ...prev, metaTitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Meta Açıklama
                    </label>
                    <textarea
                      value={pageData.metaDescription || ''}
                      onChange={(e) => setPageData(prev => ({ ...prev, metaDescription: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">
                        Şablon
                      </label>
                      <select
                        value={pageData.template || 'default'}
                        onChange={(e) => setPageData(prev => ({ ...prev, template: e.target.value as any }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      >
                        <option value="default">Varsayılan</option>
                        <option value="landing">Landing Page</option>
                        <option value="article">Makale</option>
                        <option value="legal">Yasal Metin</option>
                        <option value="json">JSON İçerik</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">
                        Yazar
                      </label>
                      <input
                        type="text"
                        value={pageData.author || 'Admin'}
                        onChange={(e) => setPageData(prev => ({ ...prev, author: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pageData.isPublished || false}
                        onChange={(e) => setPageData(prev => ({ ...prev, isPublished: e.target.checked }))}
                        className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                      />
                      <div className="flex items-center gap-1">
                        {pageData.isPublished ? (
                          <Eye size={16} className="text-green-500" />
                        ) : (
                          <EyeOff size={16} className="text-slate-400" />
                        )}
                        <span className="text-sm text-slate-700">Yayınla</span>
                      </div>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pageData.isFeatured || false}
                        onChange={(e) => setPageData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                        className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                      />
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-slate-700">Öne Çıkan</span>
                      </div>
                    </label>
                  </div>
                  
                  {pageData.template === 'json' && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Info size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-amber-800 mb-1">JSON İçerik Şablonu</h4>
                          <p className="text-sm text-amber-700">
                            Bu şablon, sayfa içeriğini JSON formatında saklar. Bileşenler otomatik olarak JSON içeriğine eklenecektir.
                            Özel JSON alanları eklemek için Sayfa Ayarları sekmesini kullanabilirsiniz.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-slate-200 flex items-center justify-between">
            <div>
              {error && (
                <div className="text-red-500 flex items-center gap-2">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
              {saveSuccess && (
                <div className="text-green-500 flex items-center gap-2">
                  <Info size={16} />
                  <span>Sayfa başarıyla kaydedildi!</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors disabled:bg-slate-300 disabled:text-slate-500"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                    <span>Kaydediliyor...</span>
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    <span>Kaydet</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageEditor;